"use client";

import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const statsData = [{
  value: 15,
  suffix: "+",
  label: "YEARS OF EXCELLENCE",
  description: "A legacy of delivering high-quality domestic buildings across the region."
}, {
  value: 200,
  suffix: "+",
  label: "HOMES CONSTRUCTED",
  description: "Bespoke residential properties built with precision and architectural integrity."
}, {
  value: 45,
  suffix: "",
  label: "EXPERT CRAFTSMEN",
  description: "Dedicated specialists ensuring every detail meets the Glencross standard."
}, {
  value: 100,
  suffix: "%",
  label: "CLIENT SATISFACTION",
  description: "Our commitment to reliability and craftsmanship is reflected in every project."
}];
const AnimatedNumber = ({
  value,
  isVisible
}: {
  value: number;
  isVisible: boolean;
}) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let startTime: number;
    const duration = 2000;
    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setCount(Math.floor(easeProgress * value));
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(value);
      }
    };
    requestAnimationFrame(animate);
  }, [value, isVisible]);
  return <>{count.toLocaleString()}</>;
};
export default function ImpactByTheNumbers() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1,
    once: true
  });
  return <section data-section-id="223893" id="the-glencross-standard" className="relative py-24 overflow-hidden dark bg-background text-foreground">
      {}
      <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1646431/img/architectural_blueprint_texture.png)] bg-cover bg-center opacity-[0.05] pointer-events-none" aria-hidden="true" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black font-default text-foreground mb-4 uppercase tracking-tighter">
            The Glencross Standard
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            Precision, reliability, and architectural excellence defined through our measurable construction milestones.
          </p>
        </div>

        <div ref={ref} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {statsData.map((stat, i) => <div data-index={i} key={i}>
              <Card className="h-full bg-transparent border-border/40 rounded-none shadow-none hover:border-primary/50 transition-colors duration-500">
                <CardContent className="p-8 flex flex-col items-center text-center justify-center h-full">
                  <div className="text-4xl md:text-5xl font-black font-default text-primary mb-3 flex items-baseline tracking-tighter">
                    <AnimatedNumber value={stat.value} isVisible={isVisible} />
                    <span>{stat.suffix}</span>
                  </div>
                  <h3 className="text-sm font-bold font-mono text-primary mb-4 tracking-widest uppercase">
                    {stat.label}
                  </h3>
                  <div className="w-full h-px bg-border/30 mb-4 relative overflow-hidden">
                    <div className={`absolute inset-y-0 left-0 bg-primary transition-all duration-1000 ease-out ${isVisible ? 'w-full' : 'w-0'}`} />
                  </div>
                  <p className="text-sm text-muted-foreground font-default leading-relaxed">
                    {stat.description}
                  </p>
                </CardContent>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}